import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface PartnerBranding {
  company_name: string;
  logo_url: string;
  theme: string;
}

const THEME_STORAGE_KEY = 'sc-b2b-theme';

// Theme name → branding config (logo and company name for pre-login auth pages)
const THEME_BRANDING: Record<string, PartnerBranding> = {
  h1bbiz: {
    company_name: 'Sapochnick Law Firm',
    logo_url: '/assets/images/h1bbiz-logo.png',
    theme: 'h1bbiz'
  }
};

@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  public currentBranding: PartnerBranding | null = null;

  get isB2b(): boolean {
    return this.currentBranding !== null;
  }

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    // Restore branding from localStorage (persists across page navigations)
    if (isPlatformBrowser(this.platformId)) {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (storedTheme) {
        this.applyTheme(storedTheme);
      }
    }
  }

  // Apply branding from ?theme= query param (password setup/reset, login page)
  applyThemeFromParam(theme: string): void {
    if (!isPlatformBrowser(this.platformId) || !theme) {
      return;
    }
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    this.applyTheme(theme);
  }

  // Apply or clear theme after login based on user's company_theme from /me/ endpoint.
  // Clears stale theme when user has no company_theme (e.g., SC user, after impersonation ends).
  applyUserTheme(theme: string | undefined): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (theme) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      this.applyTheme(theme);
    } else {
      localStorage.removeItem(THEME_STORAGE_KEY);
      this.currentBranding = null;
      Object.values(THEME_BRANDING).forEach((branding) => {
        document.body.classList.remove(branding.theme);
      });
    }
  }

  private applyTheme(theme: string): void {
    const branding = THEME_BRANDING[theme];
    if (branding) {
      this.currentBranding = branding;
      document.body.classList.add(branding.theme);
    }
  }
}
